import { TextArea } from "@package/subframe/components/TextArea";

import type { ComplianceChecklistItemDefinitionResponseDto } from "@/generated/model";
import type { ChecklistResultValue } from "@/hooks/use-offline-compliance-completion";

import { ChecklistItemRow } from "./checklist-item-row";

interface Props {
  items: ComplianceChecklistItemDefinitionResponseDto[];
  results: Record<string, { result: ChecklistResultValue; notes?: string }>;
  onResultChange: (
    itemId: string,
    result: ChecklistResultValue,
    notes?: string,
  ) => void;
  notes: string;
  onNotesChange: (notes: string) => void;
}

export function ChecklistCompletionForm({
  items,
  results,
  onResultChange,
  notes,
  onNotesChange,
}: Props) {
  const sortedItems = [...items].sort((a, b) => a.sortOrder - b.sortOrder);
  const markedCount = sortedItems.filter(
    (item) => results[item.id]?.result != null,
  ).length;

  return (
    <div className="flex w-full flex-col items-start gap-4">
      {/* Sticky progress chip — UX designer's recommendation: above-fold,
          tells the user how close they are at all times. */}
      <div className="sticky top-0 z-10 w-full bg-white py-2">
        <div className="flex items-center gap-2">
          <span className="text-body-bold font-body-bold text-neutral-900">
            Progress
          </span>
          <span className="text-caption font-caption text-subtext-color">
            {markedCount} of {sortedItems.length} marked
          </span>
        </div>
        <div
          className="mt-1 h-1 w-full overflow-hidden rounded-full bg-neutral-100"
          role="progressbar"
          aria-valuenow={markedCount}
          aria-valuemin={0}
          aria-valuemax={sortedItems.length}
        >
          <div
            className="h-full bg-primary-navy-800 transition-all"
            style={{
              width: `${sortedItems.length > 0 ? (markedCount / sortedItems.length) * 100 : 0}%`,
            }}
          />
        </div>
      </div>

      <div className="flex w-full flex-col items-start gap-3">
        {sortedItems.map((item) => (
          <ChecklistItemRow
            key={item.id}
            itemId={item.id}
            label={item.label}
            result={results[item.id]?.result}
            notes={results[item.id]?.notes}
            onResultChange={onResultChange}
          />
        ))}
      </div>

      <div className="flex w-full flex-col items-start gap-2 pt-2">
        <label className="text-body-bold font-body-bold text-neutral-900">
          Overall notes
        </label>
        <span className="text-caption font-caption text-subtext-color">
          Optional — for the inspection as a whole.
        </span>
        <TextArea className="h-auto w-full" label="" helpText="">
          <TextArea.Input
            placeholder="e.g. Inspection took longer than usual due to fire drill"
            value={notes}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              onNotesChange(e.target.value)
            }
          />
        </TextArea>
      </div>
    </div>
  );
}
